<script setup lang="ts">
import type { Schemas } from "#shopware";
import ProductListingFilter from "@/components/cms/filters/ProductListingFilter.vue";
import type {
  CmsElementProductListing,
  CmsElementSidebarFilter,
} from "@shopware/composables";
import { getTranslatedProperty } from "@shopware/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
}>();

const productListing =
  inject<Schemas["ProductListingResult"]>("productListing");

const route = useRoute();

const { t } = useI18n();
const {
  getInitialFilters,
  getSortingOrders,
  getCurrentSortingOrder,
  changeCurrentSortingOrder,
} = useListing();
const { y } = useWindowScroll({ behavior: "smooth" });

const PRIMARY_FILTER_TYPES = [""]; // use it only with sidebar filters
const openedFilter = ref<string>("");

const primaryFilters = computed(() =>
  getInitialFilters.value?.filter(({ code }) =>
    PRIMARY_FILTER_TYPES.includes(code)
  )
);
const secondaryFilters = computed(() =>
  getInitialFilters.value?.filter((filter) => isFilterNotEmpty(filter))
);

function formatFilterName(name: string) {
  if (!name) return " ";

  const DEFAULT_FILTERS_WITHOUT_TRANSLATIONS = [
    "manufacturer",
    "price",
    "rating",
    "shipping-free",
  ];

  if (DEFAULT_FILTERS_WITHOUT_TRANSLATIONS.includes(name)) {
    return t(`filter.${name}`);
  }

  const _name = useChangeCase(name, "sentenceCase");

  return _name.value;
}

async function invokeCleanFilters() {
  await navigateTo({
    query: {
      search: route.query.search,
    },
  });

  y.value = 0;
}

const isDefaultSidebarFilter =
  props.content?.type === "sidebar-filter" &&
  props.content?.config?.boxLayout?.value === "standard";

const filtersIsOpen = computed({
  get: () => route.query.sidebar === "filters",
  set: async () =>
    await navigateTo(
      { query: { ...route.query, sidebar: undefined } },
      { replace: true }
    ),
});

function toggleFilterDropdown(idOrCode: string) {
  if (openedFilter.value === idOrCode) {
    openedFilter.value = "";
  } else {
    openedFilter.value = idOrCode;
  }
}

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await navigateTo({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...route.query,
      limit: route.query.limit ? Number(route.query.limit) : 15,
    });
  },
});

const filterChips = computed(() => {
  const _chips: { id: string; label: string }[] = [];

  secondaryFilters.value?.forEach((filter) => {
    // @ts-expect-error
    (filter?.options || filter?.entities)?.filter((item) => {
      // @ts-expect-error
      if (productListing.value?.currentFilters?.properties?.includes(item.id)) {
        _chips.push({
          id: item.id,
          label: getTranslatedProperty(item, "name"),
        });
      }
    });
  });

  return _chips;
});

async function removePriceChip() {
  await navigateTo({
    query: {
      ...route.query,
      p: 1,
      "min-price": undefined,
      "max-price": undefined,
    },
  });
}

async function removeChip(chipId: string) {
  const properties = route.query?.properties;

  let updatedProperties;

  if (typeof properties === "string") {
    // If it's a string, split by "|", remove the chipId, and join back
    updatedProperties =
      properties
        .split("|")
        .filter((_id: string) => _id !== chipId)
        .join("|") || undefined;
  } else if (Array.isArray(properties)) {
    // If it's an array, filter out the chipId and join into a string
    updatedProperties =
      properties.filter((_id) => _id !== chipId).join("|") || undefined;
  }

  await navigateTo({
    query: {
      ...route.query,
      p: 1, // Reset pagination
      properties: updatedProperties,
    },
  });
}
</script>

<template>
  <div v-if="isDefaultSidebarFilter" class="lg:shadow">
    <h2 class="hidden lg:block text-xl font-semibold">
      {{ $t("search.filters") }}
    </h2>

    <Divider class="hidden lg:flex" />

    <section class="flex flex-col gap-7 pb-1">
      <ProductListingFilter
        v-for="filter in primaryFilters"
        :key="filter?.id || filter?.code"
        :selected-filters="productListing?.currentFilters"
        :filter="filter"
      />

      <ClientOnly>
        <Accordion
          v-if="secondaryFilters.length"
          :value="Array.from(Array(secondaryFilters.length).keys())"
          :multiple="true"
        >
          <AccordionPanel
            v-for="(filter, filterIndex) in secondaryFilters"
            :key="filter?.id || filter?.code"
            :value="filterIndex"
          >
            <AccordionHeader>{{
              formatFilterName(getTranslatedProperty(filter, "name"))
            }}</AccordionHeader>
            <AccordionContent>
              <ProductListingFilter
                :selected-filters="productListing?.currentFilters"
                :filter="filter"
              />
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </ClientOnly>
      
      <Button
        class="w-full bg-[#f7ebdd] border-[#f7ebdd] text-[#704530] shadow"
        :label="$t('listing.resetFilters')"
        @click="invokeCleanFilters"
      >
      </Button>
    </section>
  </div>

  <div v-else>
    <div class="flex justify-between lg:hidden py-1 gap-2">
      <Select
        v-model="currentSortingOrder"
        :options="getSortingOrders"
        :placeholder="$t('listing.sort')"
        :option-label="(data) => getTranslatedProperty(data, 'label')"
        option-value="key"
        class="ml-1 w-40"
      />

      <Button
        v-if="secondaryFilters?.length"
        :label="$t('search.filters')"
        icon="pi pi-sliders-h"
        size="small"
        @click="$router.push({ query: { ...route.query, sidebar: 'filters' } })"
      />

      <Drawer v-model:visible="filtersIsOpen">
        <template #header>
          <button
            text
            class="underline text-primary-500"
            @click="invokeCleanFilters"
          >
            {{ $t("listing.resetFilters") }}
          </button>
        </template>

        <section class="flex flex-col gap-6">
          <Accordion
            v-if="secondaryFilters?.length"
            :active-index="[0]"
            :multiple="true"
          >
            <template #default>
              <div v-for="(filter, filterIndex) in secondaryFilters">
                <AccordionPanel
                  v-if="isFilterNotEmpty(filter)"
                  :key="filter?.id || filter?.code"
                  :value="filterIndex"
                >
                  <AccordionHeader>{{
                    formatFilterName(getTranslatedProperty(filter, "name"))
                  }}</AccordionHeader>

                  <AccordionContent class="text-sm">
                    <ProductListingFilter
                      :selected-filters="productListing?.currentFilters"
                      :filter="filter"
                    />
                  </AccordionContent>
                </AccordionPanel>
              </div>
            </template>
          </Accordion>
        </section>
      </Drawer>
    </div>

    <div class="hidden lg:flex justify-between items-start gap-3 py-1">
      <div class="flex flex-wrap gap-3 ml-1 text-sm">
        <div v-for="filter in secondaryFilters" :key="filter.id">
          <Button
            v-if="isFilterNotEmpty(filter)"
            :label="formatFilterName(getTranslatedProperty(filter, 'name'))"
            :icon="
              openedFilter === (filter?.id || filter?.code)
                ? 'pi pi-chevron-right rotate-90 transition-transform'
                : 'pi pi-plus -rotate-90 transition-transform'
            "
            icon-pos="right"
            icon-class="text-xs"
            severity="secondary"
            text
            rounded
            class="text-sm"
            @click="toggleFilterDropdown(filter?.id || filter?.code)"
          />

          <div
            v-show="openedFilter === (filter?.id || filter?.code)"
            class="absolute mt-2 min-w-40"
          >
            <div
              class="bg-surface-50 dark:bg-surface-900 border rounded-lg shadow p-4 relative z-10"
            >
              <ProductListingFilter
                :selected-filters="productListing?.currentFilters"
                :filter="filter"
              />
            </div>
          </div>
        </div>
      </div>

      <Select
        v-model="currentSortingOrder"
        :options="getSortingOrders"
        :placeholder="$t('listing.sort')"
        :option-label="(data) => getTranslatedProperty(data, 'label')"
        option-value="key"
        class="mr-1 w-40"
      />
    </div>

    <div class="mt-4 flex gap-2 flex-wrap text-xs lg:px-6">
      <Chip
        v-if="
          productListing?.currentFilters?.price?.min ||
          productListing?.currentFilters?.price?.max
        "
        :label="
          $t('product.price.label') +
          (productListing?.currentFilters?.price?.min
            ? ' ' +
              $t('product.price.from') +
              ' ' +
              getFormattedPrice(productListing?.currentFilters?.price?.min)
            : '') +
          (productListing?.currentFilters?.price?.max
            ? ' ' +
              $t('product.price.to') +
              ' ' +
              getFormattedPrice(productListing?.currentFilters?.price?.max)
            : '')
        "
        removable
        @remove="removePriceChip"
      />

      <Chip
        v-for="chip in filterChips"
        :key="`${chip.id}-${chip.label}`"
        :label="getTranslatedProperty(chip, 'label')"
        removable
        @remove="removeChip(chip.id)"
      />
    </div>
  </div>
</template>
